import { isEmailAddress } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { placeLine, townLine } from '../lib/address-place'
import { useTRPC } from '../lib/trpc'
import {
  Button,
  Card,
  Field,
  FieldRow,
  Notice,
  Textarea,
  TextInput,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import { PublicNotFound } from '../ui/ErrorPage'
import { directionsHref, PublicShell, telHref } from '../ui/PublicShell'
import './contact.css'

/**
 * The one thing wrong with what has been typed, or null.
 *
 * It states what to DO rather than which field is at fault — the fields are in
 * front of the person and the rule between two of them is not.
 */
function whatIsMissing(f: {
  name: string
  email: string
  phone: string
  body: string
}): string | null {
  if (!f.name.trim()) return 'Please leave your name.'
  if (!f.body.trim()) return 'Please write a message.'
  if (!f.email.trim() && !f.phone.trim()) {
    return 'Please leave an email address or a phone number, so we can get back to you.'
  }
  // The server's own rule, so nothing it would refuse gets as far as sending.
  if (f.email.trim() && !isEmailAddress(f.email)) {
    return 'That email address does not look complete — please check it.'
  }
  return null
}

/**
 * A congregation's contact page, no login, at `/{shortId}/contact`.
 *
 * Two things, in this order: the org's own details, and a form beside them.
 * The details come first — leading on a desk, above on a phone — because
 * somebody who wants to phone has their answer without reading further, and
 * because they are what stands when the form cannot be offered.
 *
 * Which org is decided by the route map and handed in, so this page parses no
 * URLs of its own. Everything called here is a `publicProcedure`, and the write
 * is guarded by a challenge the server verifies — this page draws the widget
 * and carries its token, and decides nothing.
 */
export function ContactPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.org.list.queryOptions())
  const listed = useQuery(trpc.org.directory.queryOptions())
  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [body, setBody] = useState('')
  const [token, setToken] = useState<string | null>(null)
  /* A token is good once, so every send spends the one we hold. Bumping this
     is what asks the widget to offer the check again — without it a refused
     send leaves a solved tick above a form the server will keep refusing. */
  const [spentAt, setSpentAt] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)

  const send = useMutation(trpc.inbound.send.mutationOptions())

  // Matched on the org's own public id, never on position in the list.
  const shul = orgs.data?.find((s) => s.shortId === orgId)

  if (orgs.isLoading || !shul) {
    return (
      <PublicShell name={null} orgId={null} current="contact" onLogin={onLogin}>
        {orgs.isLoading ? (
          <div className="contact">
            <Notice>Loading…</Notice>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const postal = placeLine(shul)
  const phoneOut = shul.phone?.trim() ?? ''
  const emailOut = shul.email?.trim() ?? ''

  const submit = (e: FormEvent) => {
    e.preventDefault()
    /* Checked here as well as on the server, because a refusal costs the token
       the person has already solved — and because what the server answers a
       malformed input with is a validation report rather than a sentence. The
       server stays the authority; this only keeps somebody from spending a
       challenge on a send that cannot succeed. */
    const wrong = whatIsMissing({ name, email, phone, body })
    if (wrong) {
      setProblem(wrong)
      return
    }
    setProblem(null)
    send.mutate(
      { shortId: shul.shortId, name, email, phone, body, challenge: token },
      /* Whether the send succeeded or was refused, the token is spent — so the
         widget is reset rather than the next attempt failing on a replay. */
      { onSettled: () => setSpentAt(Date.now()) },
    )
  }

  const hasDetails = !!(postal || phoneOut || emailOut)
  const street = shul.addressLine?.trim() ?? ''
  const place = townLine(shul)

  return (
    <PublicShell
      name={shul.name}
      nameHe={shul.nameHe}
      language={shul.publicLanguage}
      orgId={shul.shortId}
      acceptsDonations={shul.acceptsDonations}
      siblings={listed.data}
      contact={shul}
      current="contact"
      onLogin={onLogin}
    >
      <div className="contact">
        <div className="contact__grid">
          <div className="contact__details">
            <div className="contact__head">
              <h1 className="contact__title">Contact us</h1>
              <p className="contact__lede">
                {/* The invitation names every way in only where every way is
                    published; the second sentence is true of any page with a
                    form on it. */}
                {postal && phoneOut && emailOut
                  ? 'Call, write, or come by. '
                  : null}
                A message sent here reaches us.
              </p>
            </div>

            {hasDetails ? (
              <dl className="contact__ways">
                {phoneOut ? (
                  <div className="contact__way">
                    <dt className="contact__label">Phone</dt>
                    <dd className="contact__value">
                      <a
                        className="contact__link contact__link--phone"
                        href={`tel:${telHref(phoneOut)}`}
                      >
                        {phoneOut}
                      </a>
                    </dd>
                  </div>
                ) : null}
                {emailOut ? (
                  <div className="contact__way">
                    <dt className="contact__label">Email</dt>
                    <dd className="contact__value">
                      <a className="contact__link" href={`mailto:${emailOut}`}>
                        {emailOut}
                      </a>
                    </dd>
                  </div>
                ) : null}
                {postal ? (
                  <div className="contact__way">
                    <dt className="contact__label">Address</dt>
                    <dd className="contact__value">
                      <address className="contact__address">
                        {street}
                        {place ? (
                          <>
                            {street ? <br /> : null}
                            {place}
                          </>
                        ) : null}
                      </address>
                      <a
                        className="contact__directions"
                        href={directionsHref(postal)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Get directions
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </dd>
                  </div>
                ) : null}
              </dl>
            ) : (
              /* An org that has filled nothing in says so, rather than showing a
                 rule over an empty block that reads as a page that failed. */
              <Notice>We haven’t published our contact details yet.</Notice>
            )}
          </div>

          <Card tone="filled" className="contact__panel">
            {send.isSuccess ? (
              /* The form is replaced rather than cleared. A blank form under a
                 confirmation reads as an invitation to send the message again,
                 and somebody unsure whether the first one went does exactly
                 that. */
              <Notice tone="info">
                Thank you — your message has reached us. We’ll get back to you
                at the email or phone you left.
              </Notice>
            ) : (
              <form className="contact__form" onSubmit={submit} noValidate>
                <h2 className="contact__subtitle">Send a message</h2>

                <Field label="Your name">
                  <TextInput
                    value={name}
                    autoComplete="name"
                    required
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>

                {/* Which way back is the sender's choice, so the pair sits
                    together and the rule between them is said beneath both
                    rather than behind a mark on one of them. */}
                <div className="contact__reply">
                  <FieldRow>
                    <Field label="Email" optional>
                      <TextInput
                        type="email"
                        value={email}
                        autoComplete="email"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Field>

                    <Field label="Phone" optional>
                      <TextInput
                        type="tel"
                        value={phone}
                        autoComplete="tel"
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Field>
                  </FieldRow>
                  <p className="contact__rule">
                    Leave one or the other, so we can get back to you.
                  </p>
                </div>

                <Field label="Message">
                  <Textarea
                    rows={6}
                    value={body}
                    required
                    onChange={(e) => setBody(e.target.value)}
                  />
                </Field>

                {problem || send.error ? (
                  <Notice tone="error">{problem ?? send.error?.message}</Notice>
                ) : null}

                <div className="contact__commit">
                  <Challenge
                    siteKey={challenge.data?.siteKey ?? null}
                    onToken={setToken}
                    spentAt={spentAt}
                  />
                  <Button
                    type="submit"
                    className="contact__send"
                    /* Where a check is drawn, Send waits for it: spending a
                       solved-once token on a submit the page can see is not
                       ready is what leaves somebody stuck. */
                    disabled={
                      send.isPending ||
                      !name.trim() ||
                      !body.trim() ||
                      (!!challenge.data?.siteKey && !token)
                    }
                  >
                    {send.isPending ? 'Sending…' : 'Send message'}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </PublicShell>
  )
}
